import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import type { Op } from 'quill/core';
import Quill, { Parchment } from 'quill/core';
import React, { useEffect, useState } from 'react';
import * as styles from './AnnotationCreate.module.scss';
import AnnotationIcon from './AnnotationIcon';
import AnnotationInput from './AnnotationInput';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import { CREATION_ID } from '../../editor/editorSlice';
import { isPrecedingCharAllowed } from '../../editor/PrefixListener';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import mentionIcon from '../../icons/non-prefilled/16x16/mention.svg';
import textIcon from '../../icons/non-prefilled/16x16/text.svg';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import showFullPageSheet from '../comment/showFullPageSheet';
import MagneticWallItem from '../MagneticWallItem';
import isContentEmpty from '../utils/isContentEmpty';

const UPDATE_ANNOTATION = gql(/** @complexity 2 */ `
  mutation updateAnnotation($id: ID!, $content: Json, $link: String, $linkText: String) {
    updateAnnotation(id: $id, content: $content, link: $link, linkText: $linkText) {
      id 
      content
      link
      linkText
    }
  }
`);

const insertText = (quill: Quill, text: string) => {
  const { index, length } = quill.getSelection(true);
  quill.insertText(index + length, text, Quill.sources.USER);
  quill.setSelection(index + length + text.length, 0, Quill.sources.USER);
};

interface AnnotationInputGroupProps {
  children: React.ReactNode;
}

const AnnotationInputGroup = ({ children }: AnnotationInputGroupProps) => (
  <div className={styles.annotationInputGroup}>{children}</div>
);

interface AnnotationInputGroupInputProps {
  value: string;
  onChange(value: string): void;
  icon: string;
  placeholder: string;
  ariaLabel: string;
}

const AnnotationInputGroupInput = ({
  value,
  onChange,
  icon,
  placeholder,
  ariaLabel,
}: AnnotationInputGroupInputProps) => {
  return (
    <div className={styles.annotationInputContainer}>
      <SVGIcon
        className={classNames(styles.icon, {
          [styles.active]: value.trim() !== '',
        })}
        size={16}
      >
        {icon}
      </SVGIcon>
      <input
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    </div>
  );
};

interface AnnotationCreateProps {
  annotationId: string;
  initialContent: Op[];
  onChange(ops: Op[]): void;
  onSaved(): void;
  link: string;
  onChangeLink: React.Dispatch<React.SetStateAction<string>>;
  linkText: string;
  onChangeLinkText: React.Dispatch<React.SetStateAction<string>>;
  onCancel: () => void;
  createAnnotation: ({
    content,
    sanitizedLink,
    linkText,
  }: {
    content: Op[];
    sanitizedLink: string | null;
    linkText: string;
  }) => void;
}

const AnnotationCreate = ({
  annotationId,
  onSaved,
  onChange,
  initialContent,
  link,
  onChangeLink,
  linkText,
  onChangeLinkText,
  onCancel,
  createAnnotation,
}: AnnotationCreateProps) => {
  const isNewAnnotation = annotationId === CREATION_ID;
  const { quill, ancestorQuill } = useQuill();
  const [content, setContent] = useState<Op[]>(initialContent);
  const sync = useAppSelector((state) => state.editor.sync);
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const sanitizedLink = sanitizeAnnotationLink(link);
  const [hasAddedLink, setHasAddedLink] = useState(false);

  useEffect(() => {
    if (sanitizedLink) {
      setHasAddedLink(true);
    }
  }, [sanitizedLink]);

  const [updateAnnotation] = useMutation(UPDATE_ANNOTATION);

  const handleSave = () => {
    if (isNewAnnotation) {
      if (!sync) throw new Error('sync not inited');
      if (!ancestorQuill) throw new Error('quill not inited');

      createAnnotation({
        content,
        sanitizedLink,
        linkText,
      });
    } else {
      const optimisticAnnotation = {
        id: annotationId,
        content,
        link: sanitizedLink,
        linkText,
      };

      updateAnnotation({
        variables: {
          id: annotationId,
          content: JSON.stringify(content),
          link: sanitizedLink,
          linkText,
        },
        optimisticResponse: {
          __typename: 'RootMutationType',
          updateAnnotation: optimisticAnnotation,
        },
      });
    }
    onSaved();
  };

  const handleChange = (newContent: Op[]) => {
    setContent(newContent);
    onChange(newContent);
  };

  const hasAutoFocus =
    (!showFullPageSheet() || isNewAnnotation) &&
    (readOnly || !quill?.hasFocus());

  const mentionUser = () => {
    if (!quill) return;
    const { index, length } = quill.getSelection(true);
    const targetIndex = index + length;
    const [leaf, offset] = quill.getLeaf(targetIndex);

    if (leaf instanceof Parchment.TextBlot) {
      const char = leaf.value()[offset - 1];
      if (!isPrecedingCharAllowed(char)) {
        insertText(quill, ' @');
        return;
      }
    }
    insertText(quill, '@');
  };

  return (
    <div className={styles.container}>
      <MagneticWallItem.WithAvatar avatar={<AnnotationIcon />}>
        <div className={styles.inputContainer}>
          <AnnotationInput
            onChange={handleChange}
            autoFocus={hasAutoFocus}
            initialContent={initialContent}
            quill={quill}
            onSave={handleSave}
          />
        </div>
      </MagneticWallItem.WithAvatar>
      <AnnotationInputGroup>
        <AnnotationInputGroupInput
          value={link}
          onChange={onChangeLink}
          icon={linkIcon}
          placeholder="Paste a reference link"
          ariaLabel="Reference link"
        />
        {hasAddedLink && (
          <AnnotationInputGroupInput
            value={linkText}
            onChange={onChangeLinkText}
            icon={textIcon}
            placeholder="Add title for reference link"
            ariaLabel="Title for reference link"
          />
        )}
      </AnnotationInputGroup>
      <div className={styles.actionsContainer}>
        <MagneticWallItem.ActionContainer>
          <MagneticWallItem.EmojiPickerButton
            onPick={(emoji) => {
              if (quill) {
                insertText(quill, emoji);
              }
            }}
          />
          {currentUser.type === UserType.Visitor ? null : (
            <MagneticWallItem.IconButton
              icon={mentionIcon}
              onClick={() => mentionUser()}
              tooltip="Mention someone"
            />
          )}

          <MagneticWallItem.ButtonGroup>
            {!isNewAnnotation && (
              <MagneticWallItem.SecondaryButton onClick={onCancel}>
                Cancel
              </MagneticWallItem.SecondaryButton>
            )}
            <MagneticWallItem.SubmitButton
              disabled={isContentEmpty(content) && !sanitizedLink}
              onClick={handleSave}
            >
              {isNewAnnotation ? 'Annotate' : 'Save'}
            </MagneticWallItem.SubmitButton>
          </MagneticWallItem.ButtonGroup>
        </MagneticWallItem.ActionContainer>
      </div>
    </div>
  );
};

export default withQuillContextProvider(AnnotationCreate);
